import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { calcOfferTotals } from "@/lib/offer-calc";
import { invalidateFor } from "@/lib/cache-registry";
import {
  EMPTY_OFFER_TOTALS,
  toCalcLines,
  type OfferDraft,
  type OfferTotalsResult,
} from "@/lib/offer-edit-calc";
import {
  upsertOfferWritingLine,
  useUpdateOffer,
  type Offer,
  type OfferLine,
} from "@/hooks/use-offers";

export type { OfferDraft, OfferTotalsResult } from "@/lib/offer-edit-calc";

interface UseOfferEditStateArgs {
  offer: Offer | undefined;
  lines: OfferLine[];
  systemHourlyRate: number;
  /**
   * Når false er tilbudet read-only (ikke-forfatter/ikke-admin). Auto-save og
   * manuell save no-op-er, så ingen draft-/total-/tilbudsskriving-mutasjon
   * persisteres. Default true (bakoverkompatibelt). VIB-offer-author-gating.
   */
  canEdit?: boolean;
}

interface UseOfferEditStateResult {
  draft: OfferDraft | null;
  setDraft: React.Dispatch<React.SetStateAction<OfferDraft | null>>;
  hourlyRate: number;
  totals: OfferTotalsResult;
  bfWithoutAutoCredit: number;
  isDirty: boolean;
  canSave: boolean;
  isSaving: boolean;
  /** Lagrer draft + total_amount + oppdaterer tilbudsskriving-linje. No-op hvis ikke canSave. */
  save: () => Promise<void>;
}

/**
 * Sentralisert draft-state for OfferEditPage. Splittet ut fra page (VIB-658)
 * for å holde page-orkestratoren under 300 linjer.
 *
 * Ansvar:
 * - Synkroniserer lokal draft med offer fra server når offer endres
 * - Beregner totals + bfWithoutAutoCredit (basis for auto-credit, ekskluderer
 *   selve auto-linja for å unngå sirkulær opp-drift)
 * - Oppdaterer tilbudsskriving-linja ved Lagre (ikke live — VIB-2098)
 * - Returnerer isDirty + canSave for save-knapp
 *
 * Funksjonell ekvivalens med tidligere inline-logikk i OfferEditPage.
 */
export function useOfferEditState({
  offer,
  lines,
  systemHourlyRate,
  canEdit = true,
}: UseOfferEditStateArgs): UseOfferEditStateResult {
  const queryClient = useQueryClient();
  const updateOffer = useUpdateOffer();
  const [draft, setDraft] = useState<OfferDraft | null>(null);

  // Synk draft med server-offer
  useEffect(() => {
    if (offer) {
      setDraft({
        title: offer.title,
        description: offer.description ?? "",
        overhead_pct: offer.overhead_pct,
        discount_pct: offer.discount_pct,
        discount_amount: offer.discount_amount,
        hourly_rate: offer.hourly_rate,
      });
    }
  }, [offer]);

  const hourlyRate = draft?.hourly_rate ?? systemHourlyRate;

  const totals = useMemo<OfferTotalsResult>(() => {
    if (!offer || !draft) return EMPTY_OFFER_TOTALS;
    // VIB-663e: totals (og dermed offer.total_amount) reflekterer kun
    // hovedlinjer — tilvalg vises som egne sub-blokker i eksport.
    const mainLines = lines.filter((l) => !l.section_id);
    return calcOfferTotals(
      {
        overhead_pct: draft.overhead_pct,
        discount_pct: draft.discount_pct,
        discount_amount: draft.discount_amount,
        include_travel_in_overhead: offer.include_travel_in_overhead,
      },
      toCalcLines(mainLines),
    );
  }, [offer, draft, lines]);

  // BF uten tilbudsskriving-linjen — brukes som basis for å beregne
  // credit. Hvis vi inkluderte auto-linjen i basis, ville BF øke for
  // hver lagring og dra crediten oppover (sirkulær).
  // Bruker også kun hovedlinjer — credit-beregning skal ikke trigges
  // av tilvalg som kunden kanskje ikke velger.
  const bfWithoutAutoCredit = useMemo(() => {
    if (!offer || !draft) return 0;
    const filtered = lines.filter(
      (l) => !l.is_auto_generated && !l.section_id,
    );
    return calcOfferTotals(
      {
        overhead_pct: draft.overhead_pct,
        discount_pct: draft.discount_pct,
        discount_amount: draft.discount_amount,
        include_travel_in_overhead: offer.include_travel_in_overhead,
      },
      toCalcLines(filtered),
    ).bf;
  }, [offer, draft, lines]);

  const isDirty = !!offer && !!draft && (
    draft.title !== offer.title ||
    (draft.description || "") !== (offer.description ?? "") ||
    draft.overhead_pct !== offer.overhead_pct ||
    draft.discount_pct !== offer.discount_pct ||
    draft.discount_amount !== offer.discount_amount ||
    draft.hourly_rate !== offer.hourly_rate
  );
  const totalChanged = !!offer && offer.total_amount !== totals.total;
  const canSave = isDirty || totalChanged;

  const save = useCallback(async () => {
    if (!canEdit) return; // read-only: ingen lagring tillatt
    if (!canSave || !draft || !offer) return;
    const title = draft.title.trim();
    if (!title) {
      toast.error("Tittel kan ikke være tom");
      return;
    }
    try {
      await updateOffer.mutateAsync({
        id: offer.id,
        patch: {
          title,
          description: draft.description.trim() || null,
          overhead_pct: draft.overhead_pct,
          discount_pct: draft.discount_pct,
          discount_amount: draft.discount_amount,
          hourly_rate: draft.hourly_rate,
          total_amount: totals.total,
        },
      });
      // Oppdater tilbudsskriving-linjen ut fra BF (eksklusiv selve
      // credit-linjen — ellers vokser den seg selv ved hver lagring).
      // Fryses ved accepted-status så tilbudsskriver beholder credit
      // fra den BF som var ved aksept-tidspunktet.
      // VIB-1136: importerte tilbud er en nøyaktig refleksjon av kilden
      // og skal ikke få tilbudsskriver-credit.
      if (offer.source !== "import") {
        await upsertOfferWritingLine({
          offerId: offer.id,
          bfAmount: bfWithoutAutoCredit,
          hourlyRate: hourlyRate ?? 2000,
          isFrozen: offer.status === "accepted",
          conceptRouting: offer.concept_routing,
        });
      }
      await invalidateFor(queryClient, "offer_lines", { offerId: offer.id });
      toast.success("Tilbud lagret");
    } catch (err) {
      toast.error(`Kunne ikke lagre: ${(err as Error).message}`);
    }
  }, [canEdit, canSave, draft, offer, totals.total, bfWithoutAutoCredit, hourlyRate, updateOffer, queryClient]);

  return {
    draft,
    setDraft,
    hourlyRate,
    totals,
    bfWithoutAutoCredit,
    isDirty,
    canSave,
    isSaving: updateOffer.isPending,
    save,
  };
}
